import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { debug } from '@/utils/debug';
import { SESSION_KEYS } from '@/config/constants';
import { queryClient } from '@/providers/QueryProvider';
import {
  applyMetaCampaignBudget,
  applyMetaCampaignRename,
  applyMetaStatusChange,
  metaActionToStatus,
} from '@/utils/optimisticMetaCache';
import type { AppConfiguration } from '@/types/api';
import type { EntityStatusAction } from '@/services/api/campaigns';

/**
 * Platform discriminator for change records that exist on both Google Ads
 * and Meta Ads. Status + name/campaign-property changes carry this so
 * `saveAllChanges()` can partition them and dispatch to the correct
 * batch endpoint.
 *
 * Other change types (FlouriSettingChange, KeywordBidChange,
 * ChannelSettingsChange) are Google-only by entity — keyword bid +
 * Demand Gen channel settings + Flouri's bid/budget toggles have no
 * Meta analog — so they don't need the discriminator.
 */
export type Platform = 'google_ads' | 'meta_ads';

/**
 * Meta supports `archive` (Meta's terminal state — no DELETE verb) where
 * Google supports `remove`. The store accepts both; the dispatcher in
 * `saveAllChanges()` routes by platform.
 */
export type MetaEntityStatusAction = 'pause' | 'enable' | 'archive';
export type AnyEntityStatusAction = EntityStatusAction | MetaEntityStatusAction;

/**
 * Pending Changes Store
 *
 * This store manages all pending changes that need to be batched and saved together.
 * Instead of making immediate API calls for each user action, changes are queued here
 * and executed in a single batch when the user clicks "Save".
 *
 * Benefits:
 * - Prevents multiple concurrent data sync jobs (pool exhaustion)
 * - Better UX with explicit save action
 * - Ability to undo changes before saving
 * - Single data sync after all changes are applied
 */

// ============================================================
// Types
// ============================================================

export type EntityType = 'campaign' | 'ad_group' | 'asset_group' | 'ad' | 'ad_set';

export interface EntityStatusChange {
  entityType: EntityType;
  entityId: string;
  campaignId: string;
  adGroupId?: string; // Required for ads
  // Action vocabulary differs by platform:
  // - Google: 'pause' | 'enable' | 'remove'
  // - Meta:   'pause' | 'enable' | 'archive'
  // The platform field below tells the dispatcher which side to route to.
  action: AnyEntityStatusAction;
  entityName?: string; // For display in UI
  // Platform discriminator. Records queued without this field default to
  // 'google_ads' at save time and emit a dev-mode console.warn flagging
  // the missed call site.
  platform?: Platform;
}

export interface CampaignPropertyChange {
  campaignId: string;
  property: 'budget' | 'name' | 'status';
  oldValue: string | number;
  newValue: string | number;
  // Platform discriminator. Same defaulting + warn behavior as
  // EntityStatusChange.
  platform?: Platform;
}

export interface FlouriSettingChange {
  entityType: 'campaign' | 'ad_group' | 'asset_group' | 'url';
  entityId: string;
  campaignId: string;
  setting: 'flouri_optimize_budget' | 'flouri_optimize_bids' | 'flouri_scraping_enabled';
  oldValue: boolean;
  newValue: boolean;
}

export interface KeywordBidChange {
  keywordId: string;
  campaignId: string;
  adGroupId: string;
  keywordText: string;
  oldBid: number;
  newBid: number;
  customerId: string; // Google Ads customer ID (required for multi-account safety)
}

export interface ChannelSettings {
  youtubeInStream: boolean;
  youtubeInFeed: boolean;
  youtubeShorts: boolean;
  discover: boolean;
  gmail: boolean;
  display: boolean;
  // maps: boolean;  // Not yet supported in Google Ads API v22/v23
}

export interface ChannelSettingsChange {
  campaignId: string;
  adGroupId: string;
  adGroupName: string;
  customerId: string;
  oldSettings: ChannelSettings;
  newSettings: ChannelSettings;
}

/**
 * Convert a dollar amount to cents, handling IEEE-754 floating point rounding issues.
 * Use Number.EPSILON to avoid cases like 1.005 * 100 rounding to 100 instead of 101.
 */
export function bidToCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100);
}

/**
 * Compare bid values at cents precision to avoid issues with micros-to-dollars
 * conversion and floating point representation. Values that differ only beyond
 * two decimal places (e.g. 1.234 vs 1.23) will be treated as equal.
 */
export function areBidsEqualInCents(oldBid: number, newBid: number): boolean {
  return bidToCents(oldBid) === bidToCents(newBid);
}

export interface PendingChanges {
  // Entity status changes (pause/enable/remove)
  statusChanges: Map<string, EntityStatusChange>;

  // Campaign property changes (budget, name)
  campaignChanges: Map<string, CampaignPropertyChange>;

  // Flouri-specific setting changes
  flouriSettingChanges: Map<string, FlouriSettingChange>;

  // Keyword bid changes (for manual CPC campaigns)
  keywordBidChanges: Map<string, KeywordBidChange>;

  // Channel settings changes (for Demand Gen campaigns)
  channelSettingsChanges: Map<string, ChannelSettingsChange>;
}

/**
 * Applied keyword bid changes that have been successfully saved to Google Ads
 * but may not yet be reflected in the React Query cache (awaiting data sync).
 * This allows the UI to display the correct values when navigating away and back
 * before the cache updates.
 */
export interface AppliedKeywordBid {
  keywordId: string;
  campaignId: string;
  adGroupId: string;
  customerId?: string; // Google Ads customer ID for multi-account support
  newBid: number;
  appliedAt: number; // timestamp for cleanup - entries older than APPLIED_BID_TTL_MS are pruned
}

// Applied bids older than this are considered stale and pruned
// 30 minutes should be plenty of time for data sync to complete
export const APPLIED_BID_TTL_MS = 30 * 60 * 1000;

/**
 * Callback function type for discard handlers.
 * Components can register these to restore their local state when changes are discarded.
 */
export type DiscardCallback = () => void;

/**
 * Keys of pending changes whose snapshot-time values were dispatched AND
 * subsequently dropped from the live queue by `saveAllChanges()` itself.
 *
 * Each per-branch cleanup block appends candidate keys as it confirms a
 * successful API write. Before returning, `saveAllChanges` runs an
 * identity-check pass against the snapshot it took at function start: a
 * candidate key whose live Map entry no longer matches its snapshot
 * value (because the user re-queued the same key mid-save) is REMOVED
 * from this list, since the queue entry must be preserved for the next
 * save. So the list reflects "API-confirmed AND queue-dropped" — not
 * just "API-confirmed."
 *
 * Consumers (SaveChangesBar via `clearDispatchedChanges`) treat this as
 * the safety-net set to ensure idempotent cleanup. Because re-queued
 * keys are filtered out here, the safety net is a true no-op for them.
 * Do NOT treat these keys as a generic "API succeeded" signal — for
 * that, check `errors` and the per-branch dispatch logic.
 */
export interface DispatchedKeys {
  statusChanges: string[];
  campaignChanges: string[];
  keywordBidChanges: string[];
  channelSettingsChanges: string[];
  flouriSettingChanges: string[];
}

export interface SaveAllChangesResult {
  success: boolean;
  errors: string[];
  dispatched: DispatchedKeys;
}

interface PendingChangesStore {
  // State
  changes: PendingChanges;
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: Date | null;
  discardCallbacks: Map<string, DiscardCallback>;

  // Applied keyword bids - persists across navigation until cache catches up
  appliedKeywordBids: Map<string, AppliedKeywordBid>;

  // Applied channel settings - persists across navigation until data refresh
  appliedChannelSettings: Map<string, ChannelSettings>;

  // Computed
  hasChanges: () => boolean;
  getChangeCount: () => number;
  getChangeSummary: () => string[];
  getPendingStatusChange: (entityType: EntityType, entityId: string, platform?: Platform) => EntityStatusChange | undefined;
  getPendingKeywordBidChange: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => KeywordBidChange | undefined;
  getAppliedKeywordBid: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => AppliedKeywordBid | undefined;

  // Actions - Queue changes
  queueStatusChange: (change: EntityStatusChange) => void;
  queueCampaignChange: (change: CampaignPropertyChange) => void;
  queueFlouriSettingChange: (change: FlouriSettingChange) => void;
  queueKeywordBidChange: (change: KeywordBidChange) => void;
  queueChannelSettingsChange: (change: ChannelSettingsChange) => void;
  getPendingChannelSettingsChange: (campaignId: string, adGroupId: string, customerId: string) => ChannelSettingsChange | undefined;
  getAppliedChannelSettings: (campaignId: string, adGroupId: string, customerId: string) => ChannelSettings | undefined;

  // Actions - Remove from queue (undo)
  removeStatusChange: (entityId: string, platform?: Platform) => void;
  removeCampaignChange: (campaignId: string, property: string, platform?: Platform) => void;
  removeFlouriSettingChange: (key: string) => void;
  removeKeywordBidChange: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => void;
  removeChannelSettingsChange: (campaignId: string, adGroupId: string, customerId: string) => void;

  // Actions - Save all changes
  saveAllChanges: () => Promise<SaveAllChangesResult>;

  // Actions - Clear
  clearAllChanges: () => void;
  clearDispatchedChanges: (dispatched: DispatchedKeys) => void;
  clearError: () => void;

  // Actions - Discard callbacks
  registerDiscardCallback: (key: string, callback: DiscardCallback) => void;
  unregisterDiscardCallback: (key: string) => void;
  executeDiscardCallbacks: () => void;

  // Actions - Applied keyword bids
  clearAppliedKeywordBid: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => void;
  clearAppliedKeywordBidsByKeys: (keys: string[]) => void;
  clearAppliedKeywordBidsForCampaign: (campaignId: string, customerId: string) => void;

  // Actions - Applied channel settings
  clearAppliedChannelSettings: (campaignId: string, adGroupId: string, customerId: string) => void;
}

// Helper to generate unique keys for changes.
//
// `platform` is part of the key so that a Google change and a Meta
// change with the same numeric IDs (e.g. campaign_id `123` on both
// platforms — realistic for small test accounts) don't overwrite each
// other in the queue Map. Missing `platform` defaults to 'google_ads'
// to match the warn-and-default shim in saveAllChanges.
const _platformSegment = (platform?: Platform): Platform =>
  platform ?? 'google_ads';

const getStatusChangeKey = (change: EntityStatusChange): string => {
  const platform = _platformSegment(change.platform);
  if (change.entityType === 'ad') {
    return `${platform}:${change.entityType}:${change.campaignId}:${change.adGroupId}:${change.entityId}`;
  }
  return `${platform}:${change.entityType}:${change.campaignId}:${change.entityId}`;
};

const getCampaignChangeKey = (
  campaignId: string,
  property: string,
  platform?: Platform,
): string => {
  return `${_platformSegment(platform)}:${campaignId}:${property}`;
};

const getFlouriSettingKey = (change: FlouriSettingChange): string => {
  return `${change.entityType}:${change.entityId}:${change.setting}`;
};

export const getChannelSettingsChangeKey = (campaignId: string, adGroupId: string, customerId: string): string => {
  // Include customerId in key to prevent collisions in multi-account scenarios
  // Campaign/ad group IDs are only unique within a Google Ads customer
  if (!customerId) {
    throw new Error('getChannelSettingsChangeKey: customerId is required for multi-account safety');
  }
  const normalizedCustomerId = customerId.replace(/\D/g, '');
  if (!normalizedCustomerId) {
    throw new Error('getChannelSettingsChangeKey: customerId must contain digits');
  }
  return `channels:${normalizedCustomerId}:${campaignId}:${adGroupId}`;
};

export const getKeywordBidChangeKey = (
  keywordId: string,
  campaignId: string,
  adGroupId: string,
  customerId: string | undefined
): string => {
  // Include customerId in key to prevent collisions in multi-account scenarios
  // Campaign/ad group/keyword IDs are only unique within a Google Ads customer
  if (!customerId) {
    throw new Error('getKeywordBidChangeKey: customerId is required for multi-account safety');
  }
  const normalizedCustomerId = customerId.replace(/\D/g, '');
  if (!normalizedCustomerId) {
    throw new Error('getKeywordBidChangeKey: customerId must contain digits');
  }
  return `keyword:${normalizedCustomerId}:${campaignId}:${adGroupId}:${keywordId}`;
};

/**
 * Check if an applied bid is stale (older than TTL).
 * Centralized staleness check to avoid logic duplication across components.
 */
export const isAppliedBidStale = (appliedBid: AppliedKeywordBid): boolean => {
  return Date.now() - appliedBid.appliedAt > APPLIED_BID_TTL_MS;
};

/**
 * Get an applied bid if it exists and is not stale.
 * Use this for direct Map lookups where you have the key.
 */
export const getValidAppliedBid = (
  appliedKeywordBids: Map<string, AppliedKeywordBid>,
  key: string
): AppliedKeywordBid | undefined => {
  const bid = appliedKeywordBids.get(key);
  if (!bid || isAppliedBidStale(bid)) {
    return undefined;
  }
  return bid;
};

// ============================================================
// Store Implementation
// ============================================================

export const usePendingChangesStore = create<PendingChangesStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      changes: {
        statusChanges: new Map(),
        campaignChanges: new Map(),
        flouriSettingChanges: new Map(),
        keywordBidChanges: new Map(),
        channelSettingsChanges: new Map(),
      },
      isSaving: false,
      saveError: null,
      lastSavedAt: null,
      discardCallbacks: new Map(),
      appliedKeywordBids: new Map(),
      appliedChannelSettings: new Map(),

      // Computed
      hasChanges: () => {
        const { changes } = get();
        return (
          changes.statusChanges.size > 0 ||
          changes.campaignChanges.size > 0 ||
          changes.flouriSettingChanges.size > 0 ||
          changes.keywordBidChanges.size > 0 ||
          changes.channelSettingsChanges.size > 0
        );
      },

      getChangeCount: () => {
        const { changes } = get();
        return (
          changes.statusChanges.size +
          changes.campaignChanges.size +
          changes.flouriSettingChanges.size +
          changes.keywordBidChanges.size +
          changes.channelSettingsChanges.size
        );
      },

      getChangeSummary: () => {
        const { changes } = get();
        const summary: string[] = [];

        // Status changes
        const removals = Array.from(changes.statusChanges.values()).filter(
          (c) => c.action === 'remove'
        );
        const archives = Array.from(changes.statusChanges.values()).filter(
          (c) => c.action === 'archive'
        );
        const pauses = Array.from(changes.statusChanges.values()).filter(
          (c) => c.action === 'pause'
        );
        const enables = Array.from(changes.statusChanges.values()).filter(
          (c) => c.action === 'enable'
        );

        if (removals.length > 0) {
          summary.push(`${removals.length} item(s) to remove`);
        }
        if (archives.length > 0) {
          summary.push(`${archives.length} item(s) to archive`);
        }
        if (pauses.length > 0) {
          summary.push(`${pauses.length} item(s) to pause`);
        }
        if (enables.length > 0) {
          summary.push(`${enables.length} item(s) to enable`);
        }

        // Campaign changes
        const budgetChanges = Array.from(changes.campaignChanges.values()).filter(
          (c) => c.property === 'budget'
        );
        const nameChanges = Array.from(changes.campaignChanges.values()).filter(
          (c) => c.property === 'name'
        );

        if (budgetChanges.length > 0) {
          summary.push(`${budgetChanges.length} budget update(s)`);
        }
        if (nameChanges.length > 0) {
          summary.push(`${nameChanges.length} name update(s)`);
        }

        // Flouri settings
        if (changes.flouriSettingChanges.size > 0) {
          summary.push(`${changes.flouriSettingChanges.size} optimization setting(s)`);
        }

        // Keyword bid changes
        if (changes.keywordBidChanges.size > 0) {
          summary.push(`${changes.keywordBidChanges.size} keyword bid(s)`);
        }

        // Channel settings changes
        if (changes.channelSettingsChanges.size > 0) {
          summary.push(`${changes.channelSettingsChanges.size} channel setting(s)`);
        }

        return summary;
      },

      getPendingStatusChange: (entityType: EntityType, entityId: string, platform?: Platform) => {
        // After the round-2 fix made queue Map keys platform-aware, the
        // Map can hold both a Google and a Meta entry with the same
        // (entityType, entityId). UI consumers must filter by platform
        // so a Meta change doesn't bleed into Google UI surfaces and
        // vice versa. Default to 'google_ads' for back-compat with
        // existing call sites (CampaignCard etc. predate the
        // discriminator and read Google-only state).
        const wantPlatform: Platform = platform ?? 'google_ads';
        const { changes } = get();
        for (const [, change] of changes.statusChanges) {
          if (
            change.entityType === entityType &&
            change.entityId === entityId &&
            (change.platform ?? 'google_ads') === wantPlatform
          ) {
            return change;
          }
        }
        return undefined;
      },

      getPendingKeywordBidChange: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => {
        const { changes } = get();
        const key = getKeywordBidChangeKey(keywordId, campaignId, adGroupId, customerId);
        return changes.keywordBidChanges.get(key);
      },

      getAppliedKeywordBid: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => {
        const { appliedKeywordBids } = get();
        const key = getKeywordBidChangeKey(keywordId, campaignId, adGroupId, customerId);
        // Use centralized staleness check
        return getValidAppliedBid(appliedKeywordBids, key);
      },

      // Queue actions
      queueStatusChange: (change: EntityStatusChange) => {
        const key = getStatusChangeKey(change);
        set((state) => {
          const newStatusChanges = new Map(state.changes.statusChanges);
          newStatusChanges.set(key, change);
          debug.log('PendingChanges: Queued status change', { key, change });
          return {
            changes: { ...state.changes, statusChanges: newStatusChanges },
          };
        });
      },

      queueCampaignChange: (change: CampaignPropertyChange) => {
        const key = getCampaignChangeKey(change.campaignId, change.property, change.platform);
        set((state) => {
          const newCampaignChanges = new Map(state.changes.campaignChanges);
          newCampaignChanges.set(key, change);
          debug.log('PendingChanges: Queued campaign change', { key, change });
          return {
            changes: { ...state.changes, campaignChanges: newCampaignChanges },
          };
        });
      },

      queueFlouriSettingChange: (change: FlouriSettingChange) => {
        const key = getFlouriSettingKey(change);
        set((state) => {
          const newFlouriChanges = new Map(state.changes.flouriSettingChanges);

          // If the new value equals the original value, remove the change instead
          if (change.oldValue === change.newValue) {
            newFlouriChanges.delete(key);
            debug.log('PendingChanges: Removed flouri change (reverted to original)', { key });
          } else {
            newFlouriChanges.set(key, change);
            debug.log('PendingChanges: Queued flouri setting change', { key, change });
          }

          return {
            changes: { ...state.changes, flouriSettingChanges: newFlouriChanges },
          };
        });
      },

      queueKeywordBidChange: (change: KeywordBidChange) => {
        const key = getKeywordBidChangeKey(change.keywordId, change.campaignId, change.adGroupId, change.customerId);
        set((state) => {
          const newKeywordBidChanges = new Map(state.changes.keywordBidChanges);

          // If the new bid equals the original bid (at cents precision), remove the change instead
          // Using cents precision avoids floating point issues from micros-to-dollars conversion
          if (areBidsEqualInCents(change.oldBid, change.newBid)) {
            newKeywordBidChanges.delete(key);
            debug.log('PendingChanges: Removed keyword bid change (reverted to original)', { key });
          } else {
            newKeywordBidChanges.set(key, change);
            debug.log('PendingChanges: Queued keyword bid change', { key, change });
          }

          return {
            changes: { ...state.changes, keywordBidChanges: newKeywordBidChanges },
          };
        });
      },

      queueChannelSettingsChange: (change: ChannelSettingsChange) => {
        const key = getChannelSettingsChangeKey(change.campaignId, change.adGroupId, change.customerId);
        set((state) => {
          const newChannelSettingsChanges = new Map(state.changes.channelSettingsChanges);

          // Check if settings are the same as original (reverted)
          const settingsMatch = Object.keys(change.oldSettings).every(
            (k) => change.oldSettings[k as keyof ChannelSettings] === change.newSettings[k as keyof ChannelSettings]
          );

          if (settingsMatch) {
            newChannelSettingsChanges.delete(key);
            debug.log('PendingChanges: Removed channel settings change (reverted to original)', { key });
          } else {
            newChannelSettingsChanges.set(key, change);
            debug.log('PendingChanges: Queued channel settings change', { key, change });
          }

          return {
            changes: { ...state.changes, channelSettingsChanges: newChannelSettingsChanges },
          };
        });
      },

      getPendingChannelSettingsChange: (campaignId: string, adGroupId: string, customerId: string) => {
        const { changes } = get();
        const key = getChannelSettingsChangeKey(campaignId, adGroupId, customerId);
        return changes.channelSettingsChanges.get(key);
      },

      getAppliedChannelSettings: (campaignId: string, adGroupId: string, customerId: string) => {
        const { appliedChannelSettings } = get();
        const key = getChannelSettingsChangeKey(campaignId, adGroupId, customerId);
        return appliedChannelSettings.get(key);
      },

      // Remove actions (undo)
      //
      // Now that queue Map keys include `platform`, an entityId-only
      // removal is ambiguous when Google + Meta share a numeric ID. To
      // preserve back-compat with existing Google-side undo call sites
      // (which never passed a platform), the caller-omitted form prefers
      // the Google entry first and only falls back to "any platform" if
      // no Google match exists. Explicit `platform` arg removes only
      // that platform's entry.
      removeStatusChange: (entityId: string, platform?: Platform) => {
        set((state) => {
          const newStatusChanges = new Map(state.changes.statusChanges);
          if (platform !== undefined) {
            // Explicit platform — exact-match removal only.
            for (const [key, change] of newStatusChanges) {
              if (
                change.entityId === entityId &&
                (change.platform ?? 'google_ads') === platform
              ) {
                newStatusChanges.delete(key);
                debug.log('PendingChanges: Removed status change', { key, platform });
                break;
              }
            }
          } else {
            // Back-compat: prefer Google, fall back to any platform.
            let removedKey: string | null = null;
            for (const [key, change] of newStatusChanges) {
              if (
                change.entityId === entityId &&
                (change.platform ?? 'google_ads') === 'google_ads'
              ) {
                newStatusChanges.delete(key);
                removedKey = key;
                break;
              }
            }
            if (removedKey === null) {
              for (const [key, change] of newStatusChanges) {
                if (change.entityId === entityId) {
                  newStatusChanges.delete(key);
                  removedKey = key;
                  break;
                }
              }
            }
            if (removedKey !== null) {
              debug.log('PendingChanges: Removed status change', { key: removedKey });
            }
          }
          return {
            changes: { ...state.changes, statusChanges: newStatusChanges },
          };
        });
      },

      removeCampaignChange: (campaignId: string, property: string, platform?: Platform) => {
        const key = getCampaignChangeKey(campaignId, property, platform);
        set((state) => {
          const newCampaignChanges = new Map(state.changes.campaignChanges);
          newCampaignChanges.delete(key);
          debug.log('PendingChanges: Removed campaign change', { key });
          return {
            changes: { ...state.changes, campaignChanges: newCampaignChanges },
          };
        });
      },

      removeFlouriSettingChange: (key: string) => {
        set((state) => {
          const newFlouriChanges = new Map(state.changes.flouriSettingChanges);
          newFlouriChanges.delete(key);
          debug.log('PendingChanges: Removed flouri setting change', { key });
          return {
            changes: { ...state.changes, flouriSettingChanges: newFlouriChanges },
          };
        });
      },

      removeKeywordBidChange: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => {
        const key = getKeywordBidChangeKey(keywordId, campaignId, adGroupId, customerId);
        set((state) => {
          const newKeywordBidChanges = new Map(state.changes.keywordBidChanges);
          newKeywordBidChanges.delete(key);
          debug.log('PendingChanges: Removed keyword bid change', { key });
          return {
            changes: { ...state.changes, keywordBidChanges: newKeywordBidChanges },
          };
        });
      },

      removeChannelSettingsChange: (campaignId: string, adGroupId: string, customerId: string) => {
        const key = getChannelSettingsChangeKey(campaignId, adGroupId, customerId);
        set((state) => {
          const newChannelSettingsChanges = new Map(state.changes.channelSettingsChanges);
          newChannelSettingsChanges.delete(key);
          debug.log('PendingChanges: Removed channel settings change', { key });
          return {
            changes: { ...state.changes, channelSettingsChanges: newChannelSettingsChanges },
          };
        });
      },

      // Save all changes
      saveAllChanges: async () => {
        // Accumulator for keys that successfully dispatched in this call.
        // Each per-branch cleanup block pushes here as it confirms a per-
        // item success. SaveChangesBar uses this to target its post-save
        // queue clear, leaving any post-snapshot additions intact.
        //
        // Per-branch tracking shapes are intentionally NOT uniform —
        // each branch records success in the shape that fits its API:
        //   - Google status:   loop-local succeededKeys[] + batched set()
        //   - Google budget/name: per-property succeededKeys[] (batched
        //                         endpoints are all-or-nothing per call)
        //   - Flouri settings: snapshot-time keys[] (load+edit+save is
        //                       atomic)
        //   - Keyword bids:   per-chunk successfullyAppliedKeys (backend
        //                     returns per-keyword status)
        //   - Channel settings: per-item succeededKeys[]
        //   - Meta status:    field-equality match inside set() (backend
        //                     response omits adGroupId so we can't
        //                     reconstruct the key directly)
        //   - Meta rename/budget: key reconstruction via
        //                         getCampaignChangeKey(id, prop, 'meta_ads')
        // Factoring these into one helper would obscure more than it
        // would share — the only common thread is "push to dispatched
        // when you drop from the live Map."
        const dispatched: DispatchedKeys = {
          statusChanges: [],
          campaignChanges: [],
          keywordBidChanges: [],
          channelSettingsChanges: [],
          flouriSettingChanges: [],
        };

        // Prevent concurrent save operations
        if (get().isSaving) {
          debug.warn('PendingChanges: Save already in progress, ignoring duplicate request');
          return { success: false, errors: ['Save already in progress'], dispatched };
        }

        const { changes, hasChanges } = get();

        if (!hasChanges()) {
          return { success: true, errors: [], dispatched };
        }

        // Capture a snapshot of every change Map at save start, by VALUE
        // identity. The bulk-drop at end-of-function uses these snapshot
        // references to decide whether each dispatched key still points at
        // its snapshot-time change object — if the user re-edited the
        // same key mid-save (queueXxx overwrites with a fresh object),
        // the live Map's value is a different reference and the bulk-drop
        // must skip it to preserve the re-edit. Without this check the
        // bulk-drop would silently wipe the post-snapshot re-edit; see
        // PR #213 Copilot feedback.
        const initialSnapshot = {
          statusChanges: new Map(changes.statusChanges),
          campaignChanges: new Map(changes.campaignChanges),
          keywordBidChanges: new Map(changes.keywordBidChanges),
          channelSettingsChanges: new Map(changes.channelSettingsChanges),
          flouriSettingChanges: new Map(changes.flouriSettingChanges),
        };

        set({ isSaving: true, saveError: null });
        const errors: string[] = [];

        try {
          const { campaignsApi } = await import('@/services/api/campaigns');
          const { optimizationApi } = await import('@/services/api/optimization');
          const { googleAdsApi } = await import('@/services/api/googleAds');
          const { metaAdsApi } = await import('@/services/api/metaAds');

          // Track if we made any Google Ads changes that need a data sync
          let madeGoogleAdsChanges = false;

          // ── Platform partition ────────────────────────────────────────
          // Status + campaign-property changes carry an optional `platform`
          // discriminator. Records without it default to 'google_ads' and
          // emit a dev-mode warn so missed call sites surface during
          // development (Theme #3 / #5 — drift between queue call site
          // and dispatch path).
          const resolvePlatform = (
            platform: Platform | undefined,
            recordType: string,
            id: string,
          ): Platform => {
            if (platform) return platform;
            if (import.meta.env.DEV) {
              // Dev-mode only: console.warn surfaces missed call sites
              // without breaking production paths. Backward-compat shim
              // per A4.2 plan §pendingChangesStore.
              console.warn(
                `[pendingChangesStore] ${recordType} queued without ` +
                `platform discriminator (id=${id}); defaulting to ` +
                `'google_ads'. Add platform: 'google_ads' or 'meta_ads' ` +
                'to the queue call site.',
              );
            }
            return 'google_ads';
          };

          const googleStatusChanges = new Map<string, EntityStatusChange>();
          const metaStatusChanges = new Map<string, EntityStatusChange>();
          for (const [key, change] of changes.statusChanges) {
            const platform = resolvePlatform(
              change.platform, 'EntityStatusChange', change.entityId,
            );
            if (platform === 'meta_ads') {
              metaStatusChanges.set(key, change);
            } else {
              googleStatusChanges.set(key, change);
            }
          }

          const googleCampaignChanges = new Map<string, CampaignPropertyChange>();
          const metaCampaignChanges = new Map<string, CampaignPropertyChange>();
          for (const [key, change] of changes.campaignChanges) {
            const platform = resolvePlatform(
              change.platform, 'CampaignPropertyChange', change.campaignId,
            );
            if (platform === 'meta_ads') {
              metaCampaignChanges.set(key, change);
            } else {
              googleCampaignChanges.set(key, change);
            }
          }

          // 1. Execute status changes (these go directly to Google Ads)
          // ALL status changes skip data sync - we'll trigger ONE sync at the end
          //
          // Per-item tracking: on each successful dispatch we push the key
          // into `dispatched.statusChanges`. The actual queue drop is
          // deferred to a single `set()` at the very end of saveAllChanges
          // — keeping the live queue populated during dispatch means
          // hasChanges() stays true throughout the save, so SaveChangesBar
          // doesn't schedule a mid-save exit animation (which would cause
          // a visible flicker if the user queues a post-snapshot change
          // during the in-flight save). See the end-of-function bulk drop.
          for (const [key, change] of googleStatusChanges) {
            try {
              debug.log('PendingChanges: Executing status change (skip_data_sync=true)', { key, change });

              // Google's API only accepts 'pause' | 'enable' | 'remove'.
              // 'archive' is Meta-only; if it slips through here (caller
              // bug — Google change tagged with a Meta action), skip the
              // dispatch and surface an error rather than 400 from the
              // backend.
              if (change.action === 'archive') {
                errors.push(
                  `Cannot dispatch 'archive' to Google Ads — action is Meta-only.`,
                );
                continue;
              }
              const googleAction = change.action as EntityStatusAction;

              if (change.entityType === 'ad_group') {
                await campaignsApi.updateAdGroupStatus(
                  change.campaignId,
                  change.entityId,
                  googleAction,
                  true // skipDataSync - we'll trigger ONE sync at the end
                );
                madeGoogleAdsChanges = true;
                dispatched.statusChanges.push(key);
              } else if (change.entityType === 'asset_group') {
                await campaignsApi.updateAssetGroupStatus(
                  change.campaignId,
                  change.entityId,
                  googleAction,
                  true // skipDataSync
                );
                madeGoogleAdsChanges = true;
                dispatched.statusChanges.push(key);
              } else if (change.entityType === 'ad' && change.adGroupId) {
                await campaignsApi.updateAdStatus(
                  change.campaignId,
                  change.adGroupId,
                  change.entityId,
                  googleAction,
                  true, // skipDataSync
                );
                madeGoogleAdsChanges = true;
                dispatched.statusChanges.push(key);
              } else if (change.entityType === 'campaign') {
                await campaignsApi.updateCampaignStatus(
                  change.campaignId,
                  googleAction,
                  true // skipDataSync
                );
                madeGoogleAdsChanges = true;
                dispatched.statusChanges.push(key);
              } else {
                // Loud failure on the silent-drop path. The Google dispatcher
                // historically fell through this with no-op + success=true,
                // so a Meta entity type (e.g. 'ad_set') queued without
                // platform — and thus defaulted to 'google_ads' by the
                // backward-compat shim — would be quietly lost. Now we
                // surface it as an explicit error so the save reports
                // success=false and the caller can investigate.
                //
                // Reaches here for:
                // - 'ad_set' (Meta-only entity routed to Google dispatcher)
                // - 'ad' with missing adGroupId (caller bug)
                // - any future entityType added to EntityType without a
                //   matching Google branch
                const entityName = change.entityName || change.entityId;
                const errorMsg =
                  `Cannot dispatch ${change.entityType} status change ` +
                  `"${entityName}" to Google Ads ` +
                  (change.entityType === 'ad' && !change.adGroupId
                    ? '(ad change is missing adGroupId).'
                    : `(unsupported entityType for Google).`);
                errors.push(errorMsg);
                debug.error('PendingChanges: Unhandled Google entityType', {
                  key, change,
                });
              }
            } catch (error) {
              const entityName = change.entityName || change.entityId;
              const errorMsg = `Failed to ${change.action} ${change.entityType} "${entityName}"`;
              errors.push(errorMsg);
              debug.error('PendingChanges: Status change failed', { key, error });
            }
          }

          // Queue drop deferred to end-of-function bulk set(); the
          // succeeded keys live in `dispatched.statusChanges` until then.

          // 2. Execute campaign property changes (budget, name)
          //
          // Track keys per property so we can clean up the live queue
          // per-batch on success (mirrors the Google status + Meta
          // patterns elsewhere in this function). The batched endpoints
          // are all-or-nothing per call, so on success we drop every key
          // that was in the call; on failure we leave them all queued
          // for retry.
          const budgetUpdates: Record<string, number> = {};
          const nameUpdates: Record<string, string> = {};
          const budgetChangeKeys: string[] = [];
          const nameChangeKeys: string[] = [];

          for (const [key, change] of googleCampaignChanges) {
            if (change.property === 'budget') {
              budgetUpdates[change.campaignId] = change.newValue as number;
              budgetChangeKeys.push(key);
            } else if (change.property === 'name') {
              nameUpdates[change.campaignId] = change.newValue as string;
              nameChangeKeys.push(key);
            }
          }

          if (Object.keys(budgetUpdates).length > 0) {
            try {
              await optimizationApi.updateManualBudgets({
                campaign_budget_updates: budgetUpdates,
              });
              debug.log('PendingChanges: Budget updates saved', budgetUpdates);
              madeGoogleAdsChanges = true;
              dispatched.campaignChanges.push(...budgetChangeKeys);
            } catch (error) {
              errors.push('Failed to update campaign budgets');
              debug.error('PendingChanges: Budget updates failed', error);
            }
          }

          if (Object.keys(nameUpdates).length > 0) {
            try {
              await optimizationApi.updateManualNames({
                campaign_name_updates: nameUpdates,
              });
              debug.log('PendingChanges: Name updates saved', nameUpdates);
              madeGoogleAdsChanges = true;
              dispatched.campaignChanges.push(...nameChangeKeys);
            } catch (error) {
              errors.push('Failed to update campaign names');
              debug.error('PendingChanges: Name updates failed', error);
            }
          }

          // Queue drop deferred to end-of-function bulk set().

          // 3. Save Flouri settings to app_configuration
          // These don't trigger Google Ads API calls, just config updates.
          //
          // Snapshot semantic: the load + edit + save sequence is one
          // atomic operation. Capture the key set at function entry so
          // a post-snapshot queue addition isn't included in the
          // success-cleanup. (Captured here rather than at the top of
          // saveAllChanges because we only need it inside this branch.)
          const flouriSnapshotKeys = Array.from(changes.flouriSettingChanges.keys());
          if (changes.flouriSettingChanges.size > 0) {
            try {
              const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
              if (!memberUuid) {
                throw new Error('Member UUID not found');
              }

              // Load current configuration
              const currentConfig = await optimizationApi.loadConfiguration(memberUuid);
              const campaignList = currentConfig.configuration?.campaign_list || [];

              // Apply each flouri setting change
              for (const [, change] of changes.flouriSettingChanges) {
                debug.log('PendingChanges: Applying flouri setting change', change);

                if (change.setting === 'flouri_optimize_bids' && change.entityType === 'ad_group') {
                  // Find the campaign and ad group, then update flouri_optimize_bids
                  const campaign = campaignList.find(c => c.campaign_id === change.campaignId);
                  if (campaign?.ads_group_list) {
                    const adGroup = campaign.ads_group_list.find(
                      ag => (ag.ad_group_id || ag.ads_group_id) === change.entityId
                    );
                    if (adGroup) {
                      adGroup.flouri_optimize_bids = change.newValue;
                      debug.log('PendingChanges: Updated flouri_optimize_bids', {
                        campaignId: change.campaignId,
                        adGroupId: change.entityId,
                        newValue: change.newValue
                      });
                    }
                  }
                } else if (change.setting === 'flouri_optimize_budget' && change.entityType === 'campaign') {
                  // Update campaign-level flouri_optimize_budget
                  const campaign = campaignList.find(c => c.campaign_id === change.entityId);
                  if (campaign) {
                    campaign.flouri_optimize_budget = change.newValue;
                    debug.log('PendingChanges: Updated flouri_optimize_budget', {
                      campaignId: change.entityId,
                      newValue: change.newValue
                    });
                  }
                }
                // Add more setting types as needed (flouri_scraping_enabled, etc.)
              }

              // Save the updated configuration
              await optimizationApi.saveConfiguration({
                configuration: {
                  ...currentConfig.configuration,
                  campaign_list: campaignList
                }
              });
              debug.log('PendingChanges: Flouri settings saved successfully');

              // Track snapshotted keys in dispatched; the actual queue
              // drop is deferred to end-of-function bulk set().
              dispatched.flouriSettingChanges.push(...flouriSnapshotKeys);

            } catch (error) {
              errors.push('Failed to save optimization settings');
              debug.error('PendingChanges: Flouri settings save failed', error);
            }
          }

          // 4. Execute keyword bid changes (batch update)
          // Group changes by customerId to handle multi-account scenarios
          if (changes.keywordBidChanges.size > 0) {
            try {
              const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
              if (!memberUuid) {
                throw new Error('Member UUID not found');
              }

              // Group changes by normalized customerId.
              // Normalize customerId to digits-only to avoid splitting the same account
              // into multiple groups due to format differences (e.g., '123-456-7890' vs '1234567890').
              // Changes without a valid customerId are treated as validation errors and removed
              // from the store to prevent the user from hitting the same error on retry.
              const changesByCustomerId = new Map<string, KeywordBidChange[]>();
              const invalidChangeKeys: string[] = [];
              // Iterate over entries to get both key and value - avoids calling getKeywordBidChangeKey
              // on invalid entries (which would throw since customerId is now required)
              for (const [key, change] of changes.keywordBidChanges.entries()) {
                const normalizedCustomerId = change.customerId?.replace(/\D/g, '') || '';
                // Validate customer ID is exactly 10 digits (Google Ads requirement).
                // The backend will 400 on invalid formats, so we remove them here to
                // prevent repeated save failures with the same queued changes.
                if (!/^\d{10}$/.test(normalizedCustomerId)) {
                  // Use the existing key from the Map entry (safe even if customerId is invalid)
                  invalidChangeKeys.push(key);
                  debug.error('PendingChanges: Removing keyword bid change with missing or invalid customerId', {
                    keywordId: change.keywordId,
                    campaignId: change.campaignId,
                    customerId: change.customerId
                  });
                  continue;
                }
                const group = changesByCustomerId.get(normalizedCustomerId) || [];
                group.push(change);
                changesByCustomerId.set(normalizedCustomerId, group);
              }

              // Remove invalid changes from store so user doesn't hit the same error on retry
              if (invalidChangeKeys.length > 0) {
                set((state) => {
                  const newKeywordBidChanges = new Map(state.changes.keywordBidChanges);
                  for (const key of invalidChangeKeys) {
                    newKeywordBidChanges.delete(key);
                  }
                  return {
                    changes: { ...state.changes, keywordBidChanges: newKeywordBidChanges }
                  };
                });
                errors.push(`${invalidChangeKeys.length} keyword bid change(s) removed: missing or invalid Google Ads account ID.`);
              }

              debug.log('PendingChanges: Batch updating keyword bids', {
                totalCount: changes.keywordBidChanges.size,
                accountCount: changesByCustomerId.size
              });

              // Process each account's changes separately, chunking to respect backend limit
              const MAX_BID_UPDATES_PER_REQUEST = 50;

              // Track successfully applied keyword IDs to remove from store
              const successfullyAppliedKeys: string[] = [];

              for (const [customerId, accountChanges] of changesByCustomerId) {
                // Build a map from keyword_id to all associated change keys for clearing successful updates.
                // The store key includes campaignId/adGroupId, so there can be multiple pending changes
                // for the same keywordId. The backend batch-update endpoint expects at most one update
                // per keyword_id, so we deduplicate before sending.
                const keywordIdToKeys = new Map<string, string[]>();
                for (const change of accountChanges) {
                  const key = getKeywordBidChangeKey(change.keywordId, change.campaignId, change.adGroupId, change.customerId);
                  const existingKeys = keywordIdToKeys.get(change.keywordId);
                  if (existingKeys) {
                    existingKeys.push(key);
                  } else {
                    keywordIdToKeys.set(change.keywordId, [key]);
                  }
                }

                // Deduplicate changes by keywordId before building bidUpdates.
                // If multiple pending changes exist for the same keyword, keep the latest one.
                const dedupedChangesByKeywordId = new Map<string, (typeof accountChanges)[number]>();
                for (const change of accountChanges) {
                  dedupedChangesByKeywordId.set(change.keywordId, change);
                }
                const allDedupedChanges = Array.from(dedupedChangesByKeywordId.values());

                // Filter out temporary/non-numeric keyword IDs (e.g., 'temp-*' for newly added keywords).
                // The backend requires digits-only keyword IDs; sending temp IDs would cause a 422 error.
                const invalidIdChanges = allDedupedChanges.filter(change => !/^\d+$/.test(change.keywordId));
                if (invalidIdChanges.length > 0) {
                  debug.warn('PendingChanges: Skipping keyword bid updates with non-numeric IDs (not yet synced)', {
                    customerId,
                    skippedCount: invalidIdChanges.length,
                    invalidKeywordIds: invalidIdChanges.map(c => c.keywordId)
                  });
                }
                const dedupedChanges = allDedupedChanges.filter(change => /^\d+$/.test(change.keywordId));

                // If no valid changes remain after filtering, handle specially.
                if (dedupedChanges.length === 0) {
                  // If we *only* had invalid IDs (e.g., temp-* keywords not yet synced),
                  // surface an error so we don't silently drop the user's edits
                  if (invalidIdChanges.length > 0) {
                    debug.error('PendingChanges: All keyword bid updates filtered out due to non-numeric IDs', {
                      customerId,
                      invalidKeywordIds: invalidIdChanges.map(c => c.keywordId)
                    });
                    errors.push('Some keyword bid changes could not be saved because the keywords are not yet synced. Please wait for sync to complete and try again.');
                  } else {
                    debug.log('PendingChanges: No valid keyword bid updates for account after filtering', { customerId });
                  }
                  continue;
                }

                const bidUpdates = dedupedChanges.map(change => ({
                  keyword_id: change.keywordId,
                  new_bid: change.newBid
                }));

                debug.log('PendingChanges: Processing keyword bids for account', {
                  customerId,
                  count: bidUpdates.length
                });

                // Track aggregated results across chunks
                let totalSuccessfulCount = 0;
                let totalFailedCount = 0;

                // Process in chunks to respect backend per-request limit
                for (let i = 0; i < bidUpdates.length; i += MAX_BID_UPDATES_PER_REQUEST) {
                  const chunk = bidUpdates.slice(i, i + MAX_BID_UPDATES_PER_REQUEST);

                  debug.log('PendingChanges: Processing keyword bids chunk', {
                    customerId,
                    chunkSize: chunk.length,
                    offset: i,
                    totalUpdates: bidUpdates.length
                  });

                  // Skip data sync here - we'll trigger ONE sync at the end
                  const result = await googleAdsApi.batchUpdateKeywordBids(memberUuid, chunk, true, customerId);

                  if (result.success) {
                    debug.log('PendingChanges: Keyword bid updates chunk saved', {
                      customerId,
                      successful: result.successful_updates.length,
                      failed: result.failed_updates.length
                    });

                    totalSuccessfulCount += result.successful_updates.length;
                    totalFailedCount += result.failed_updates.length;

                    // Track successfully applied changes for removal from store
                    for (const update of result.successful_updates) {
                      // Find all keys for this keyword_id (there should typically be one)
                      const keys = keywordIdToKeys.get(update.keyword_id);
                      if (keys) {
                        successfullyAppliedKeys.push(...keys);
                      }
                    }

                    if (result.failed_updates.length > 0) {
                      debug.warn('PendingChanges: Some keyword bid updates in chunk failed', result.failed_updates);
                    }
                  } else {
                    totalFailedCount += chunk.length;
                    debug.error('PendingChanges: Keyword bid updates chunk failed', {
                      customerId,
                      offset: i,
                      error: result
                    });
                  }
                }

                // Mark as having Google Ads changes if at least one update succeeded
                if (totalSuccessfulCount > 0) {
                  madeGoogleAdsChanges = true;
                }

                // Surface any failures to the user (aggregated across chunks)
                // Note: We still push errors for partial failures so success: false is returned,
                // but madeGoogleAdsChanges remains true if any succeeded so data sync still runs
                if (totalFailedCount > 0 && bidUpdates.length > 0) {
                  const accountSuffix = customerId ? ` for account ${customerId}` : '';
                  errors.push(`${totalFailedCount} keyword bid update(s) failed${accountSuffix}`);
                  debug.warn('PendingChanges: Some keyword bid updates failed', {
                    customerId,
                    failedCount: totalFailedCount,
                    successfulCount: totalSuccessfulCount
                  });
                }
              }

              // Prune stale applied bids unconditionally to prevent unbounded growth
              // This runs regardless of whether there are successful updates
              set((state) => {
                const newAppliedKeywordBids = new Map(state.appliedKeywordBids);
                let prunedCount = 0;
                for (const [key, bid] of newAppliedKeywordBids) {
                  if (isAppliedBidStale(bid)) {
                    newAppliedKeywordBids.delete(key);
                    prunedCount++;
                  }
                }
                if (prunedCount > 0) {
                  debug.log('PendingChanges: Pruned stale applied keyword bids', { prunedCount });
                  return { appliedKeywordBids: newAppliedKeywordBids };
                }
                return {}; // No changes needed
              });

              // Move successfully applied keyword bid changes to the
              // applied store. The pending-queue drop is deferred to the
              // end-of-function bulk set() (keeps the bar's count stable
              // during in-flight save). The applied-store write stays
              // here — the applied values are read by KeywordsPanel for
              // session-persistent UI of just-saved values, and that
              // surface needs them visible the moment the API confirms.
              if (successfullyAppliedKeys.length > 0) {
                dispatched.keywordBidChanges.push(...successfullyAppliedKeys);
                set((state) => {
                  const newAppliedKeywordBids = new Map(state.appliedKeywordBids);

                  for (const key of successfullyAppliedKeys) {
                    // Read from the initial snapshot — NOT the live Map.
                    // If the user re-queues the same key mid-save, the
                    // live entry holds the re-edit, but what was actually
                    // sent to the API + confirmed is the snapshot value.
                    // Writing the re-edit into appliedKeywordBids would
                    // leave an incorrect overlay after Discard (which
                    // doesn't clear appliedKeywordBids).
                    const change = initialSnapshot.keywordBidChanges.get(key);
                    if (change) {
                      newAppliedKeywordBids.set(key, {
                        keywordId: change.keywordId,
                        campaignId: change.campaignId,
                        adGroupId: change.adGroupId,
                        customerId: change.customerId,
                        newBid: change.newBid,
                        appliedAt: Date.now(),
                      });
                    }
                  }

                  debug.log('PendingChanges: Moved successfully applied keyword bid changes to applied store', {
                    movedCount: successfullyAppliedKeys.length,
                    appliedTotal: newAppliedKeywordBids.size
                  });

                  return {
                    appliedKeywordBids: newAppliedKeywordBids
                  };
                });
              }
            } catch (error) {
              errors.push('Failed to update keyword bids');
              debug.error('PendingChanges: Keyword bid updates failed', error);
            }
          }

          // 5. Execute channel settings changes (Demand Gen)
          if (changes.channelSettingsChanges.size > 0) {
            const successfulChannelSettingsKeys: string[] = [];
            try {
              const { campaignsApi } = await import('@/services/api/campaigns');

              for (const [key, change] of changes.channelSettingsChanges) {
                try {
                  debug.log('PendingChanges: Updating channel settings (skip_data_sync=true)', { key, change });

                  // Convert camelCase to snake_case for API
                  // Note: maps is not yet supported in Google Ads API v22/v23
                  await campaignsApi.updateAdGroupChannelSettings(
                    change.campaignId,
                    change.adGroupId,
                    {
                      youtube_in_stream: change.newSettings.youtubeInStream,
                      youtube_in_feed: change.newSettings.youtubeInFeed,
                      youtube_shorts: change.newSettings.youtubeShorts,
                      discover: change.newSettings.discover,
                      gmail: change.newSettings.gmail,
                      display: change.newSettings.display,
                    },
                    true // skipDataSync - we'll trigger ONE sync at the end
                  );
                  madeGoogleAdsChanges = true;
                  successfulChannelSettingsKeys.push(key);
                  debug.log('PendingChanges: Channel settings updated successfully', { key });
                } catch (error) {
                  const errorMsg = `Failed to update channel settings for "${change.adGroupName}"`;
                  errors.push(errorMsg);
                  debug.error('PendingChanges: Channel settings update failed', { key, error });
                }
              }

              // Move successfully applied channel settings to the applied
              // store. Pending-queue drop deferred to end-of-function bulk
              // set() — same rationale as keyword bids above.
              if (successfulChannelSettingsKeys.length > 0) {
                dispatched.channelSettingsChanges.push(...successfulChannelSettingsKeys);
                set((state) => {
                  const newAppliedChannelSettings = new Map(state.appliedChannelSettings);

                  for (const key of successfulChannelSettingsKeys) {
                    // Read from initial snapshot — same reason as the
                    // keyword bid block above: a mid-save re-edit would
                    // otherwise leak into appliedChannelSettings (which
                    // discard doesn't clear) and surface as a stale
                    // overlay post-discard.
                    const change = initialSnapshot.channelSettingsChanges.get(key);
                    if (change) {
                      newAppliedChannelSettings.set(key, change.newSettings);
                    }
                  }

                  debug.log('PendingChanges: Moved channel settings to applied store', {
                    movedCount: successfulChannelSettingsKeys.length,
                    appliedTotal: newAppliedChannelSettings.size
                  });

                  return {
                    appliedChannelSettings: newAppliedChannelSettings
                  };
                });
              }
            } catch (error) {
              errors.push('Failed to update channel settings');
              debug.error('PendingChanges: Channel settings updates failed', error);
            }
          }

          // 5b. Execute Meta status + rename changes via a single batch
          // round trip. Two failure paths, both terminating in the
          // `errors` array on this outer scope:
          //
          // - Per-entity failures: backend returns 200 with the failed
          //   entities in `result.failed_updates`; each one is pushed
          //   to `errors` so the SaveChangesBar's per-row toast UX
          //   shows them.
          // - Whole-batch failures (auth, 5xx, network): the IIFE's
          //   own try/catch wraps the `metaAdsApi.batchUpdate` call and
          //   pushes a single rolled-up error to `errors`. The error
          //   does NOT propagate to the outer `try` block at the top
          //   of saveAllChanges — that catch is reserved for save-loop
          //   bugs, not per-platform dispatch failures.
          //
          // Meta-only — no Google Ads data sync gate (Meta has its own
          // daily ingest pipeline; nothing needs to refetch immediately
          // here beyond the React Query invalidation the SaveChangesBar
          // triggers on success). Runs in parallel with the Google data
          // sync below via the Promise.allSettled at step 6.
          let metaSavePromise: Promise<void> | null = null;
          if (metaStatusChanges.size > 0 || metaCampaignChanges.size > 0) {
            // metaAdsApi.batchUpdate keeps `memberUuid` as a positional
            // arg only for parity with `googleAdsApi.batchUpdateKeywordBids`
            // — the backend reads member_id from the JWT, not the
            // payload (see the `_memberUuid` underscore-prefix in
            // metaAds.ts). So we don't gate the Meta save on
            // localStorage having a MEMBER_UUID — that just blocks a
            // valid request for a stale-localStorage reason. Pass
            // through whatever's there (or empty string) and let the
            // backend authoritative.
            const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID) ?? '';
            {
              // Build the wire payload with runtime validation per item.
              // Previously this used `as` casts that silently translated
              // 'ad_group' → 'ad_set' and 'remove' → 'archive'. Copilot
              // flagged that an unsupported entityType (e.g. 'asset_group')
              // or unsupported action would produce an invalid wire
              // payload, 422-ing the entire batch and blocking all Meta
              // updates. We now filter to the explicit supported sets
              // and push a per-record error for anything excluded — so
              // the rest of the batch still ships.
              const META_ENTITY_TYPES = new Set(['campaign', 'ad_set', 'ad']);
              const META_ACTIONS = new Set(['pause', 'enable', 'archive']);

              // Collect keys of records that fail the runtime filter so
              // we can drop them from the queue once we're done iterating.
              // Without this, every subsequent save retry would re-emit
              // the same error forever (the invalid record stays queued,
              // saveAllChanges returns success=false, SaveChangesBar's
              // success-only `clearDispatchedChanges` never fires).
              // Mirrors the keyword-bid invalid-removal pattern earlier
              // in this function.
              const invalidMetaStatusKeys: string[] = [];
              const invalidMetaCampaignKeys: string[] = [];

              const metaStatusPayload: Array<{
                entity_type: 'campaign' | 'ad_set' | 'ad';
                entity_id: string;
                action: 'pause' | 'enable' | 'archive';
              }> = [];
              for (const [key, c] of metaStatusChanges) {
                if (!META_ENTITY_TYPES.has(c.entityType)) {
                  errors.push(
                    `Meta change skipped: unsupported entityType ` +
                    `'${c.entityType}' for "${c.entityName ?? c.entityId}".`,
                  );
                  debug.warn('PendingChanges: Skipping invalid Meta entityType', {
                    entityType: c.entityType, entityId: c.entityId,
                  });
                  invalidMetaStatusKeys.push(key);
                  continue;
                }
                if (!META_ACTIONS.has(c.action)) {
                  errors.push(
                    `Meta change skipped: unsupported action ` +
                    `'${c.action}' for "${c.entityName ?? c.entityId}".`,
                  );
                  debug.warn('PendingChanges: Skipping invalid Meta action', {
                    action: c.action, entityId: c.entityId,
                  });
                  invalidMetaStatusKeys.push(key);
                  continue;
                }
                metaStatusPayload.push({
                  entity_type: c.entityType as 'campaign' | 'ad_set' | 'ad',
                  entity_id: c.entityId,
                  action: c.action as 'pause' | 'enable' | 'archive',
                });
              }

              const metaRenamePayload: Array<{
                entity_type: 'campaign';
                entity_id: string;
                name: string;
              }> = [];
              const metaBudgetPayload: Array<{
                entity_type: 'campaign';
                entity_id: string;
                daily_budget: number;
              }> = [];
              for (const [key, c] of metaCampaignChanges) {
                if (c.property === 'name') {
                  const newName = c.newValue;
                  if (typeof newName !== 'string') {
                    errors.push(
                      `Meta rename skipped: non-string name for campaign ${c.campaignId}.`,
                    );
                    invalidMetaCampaignKeys.push(key);
                    continue;
                  }
                  metaRenamePayload.push({
                    // CampaignPropertyChange is campaign-scoped at queue
                    // time (the only rename surface on the MetaCampaignCard
                    // is the campaign itself); ad-set / ad rename UI lands
                    // with the A4.3 wizards via a different change type.
                    entity_type: 'campaign',
                    entity_id: c.campaignId,
                    name: newName,
                  });
                } else if (c.property === 'budget') {
                  // Phase 1b — CBO daily-budget edits from MetaCampaignCard's
                  // BudgetBar land here. Backend MetaBatchBudgetChange
                  // enforces `gt=0` and Literal["campaign"] entity_type;
                  // mirror those gates client-side so a malformed queue
                  // entry surfaces as an error per-record rather than
                  // 422-ing the entire batch. Budgets travel in MAJOR
                  // units — the backend converts to Meta wire-shape
                  // minor units at the API boundary.
                  const newBudget = c.newValue;
                  if (typeof newBudget !== 'number' || !Number.isFinite(newBudget)) {
                    errors.push(
                      `Meta budget skipped: non-numeric value for campaign ${c.campaignId}.`,
                    );
                    invalidMetaCampaignKeys.push(key);
                    continue;
                  }
                  if (newBudget <= 0) {
                    errors.push(
                      `Meta budget skipped: must be > 0 for campaign ${c.campaignId}.`,
                    );
                    invalidMetaCampaignKeys.push(key);
                    continue;
                  }
                  metaBudgetPayload.push({
                    entity_type: 'campaign',
                    entity_id: c.campaignId,
                    daily_budget: newBudget,
                  });
                } else {
                  // 'status' on campaignChanges is structurally allowed by
                  // CampaignPropertyChange but Meta status edits flow via
                  // statusChanges (queueStatusChange). Surface as an
                  // explicit error so a queue call site bug doesn't
                  // silently drop the change.
                  errors.push(
                    `Meta change skipped: unsupported property ` +
                    `'${c.property}' for campaign ${c.campaignId} ` +
                    `(expected 'name' or 'budget').`,
                  );
                  debug.warn('PendingChanges: Skipping unsupported Meta property', {
                    property: c.property, campaignId: c.campaignId,
                  });
                  invalidMetaCampaignKeys.push(key);
                }
              }

              // Drop the invalid Meta records from the queue immediately
              // so the user doesn't get stuck in a failure loop on retry.
              if (invalidMetaStatusKeys.length > 0 || invalidMetaCampaignKeys.length > 0) {
                set((state) => {
                  const nextStatus = new Map(state.changes.statusChanges);
                  const nextCampaign = new Map(state.changes.campaignChanges);
                  for (const key of invalidMetaStatusKeys) nextStatus.delete(key);
                  for (const key of invalidMetaCampaignKeys) nextCampaign.delete(key);
                  return {
                    changes: {
                      ...state.changes,
                      statusChanges: nextStatus,
                      campaignChanges: nextCampaign,
                    },
                  };
                });
                debug.log('PendingChanges: Removed invalid Meta changes from queue', {
                  statusKeysDropped: invalidMetaStatusKeys.length,
                  campaignKeysDropped: invalidMetaCampaignKeys.length,
                });
              }

              // If the runtime filter dropped every Meta change, skip
              // the HTTP call entirely — the errors array already
              // carries the per-record explanations.
              if (
                metaStatusPayload.length === 0 &&
                metaRenamePayload.length === 0 &&
                metaBudgetPayload.length === 0
              ) {
                debug.log(
                  'PendingChanges: All Meta changes filtered out; skipping batch',
                );
              } else {
              metaSavePromise = (async () => {
                try {
                  debug.log('PendingChanges: Dispatching Meta batch', {
                    status: metaStatusPayload.length,
                    rename: metaRenamePayload.length,
                    budget: metaBudgetPayload.length,
                  });
                  const result = await metaAdsApi.batchUpdate(
                    memberUuid,
                    metaStatusPayload,
                    metaRenamePayload,
                    metaBudgetPayload,
                  );
                  if (result.failed_updates.length > 0) {
                    for (const failed of result.failed_updates) {
                      errors.push(
                        `Meta ${failed.entity_type} ${failed.operation} ` +
                        `failed: ${failed.error ?? 'unknown error'}`,
                      );
                    }
                  }
                  // Clean up successfully-applied entries from the
                  // pendingChangesStore queue (and the Meta settings
                  // store, for renames). Without this, a partial-
                  // failure save (failed_updates non-empty → success=
                  // false → SaveChangesBar skips its success-path
                  // `clearDispatchedChanges`) would leave the succeeded
                  // entries queued, so the next save retry would re-
                  // send them + the UI would stay in a perpetual
                  // "pending" state. We handle status AND rename ops
                  // here so both paths are partial-failure-safe.

                  // Status: drop succeeded ones from statusChanges Map.
                  const succeededStatusUpdates = result.successful_updates.filter(
                    (u) => u.operation === 'status',
                  );
                  if (succeededStatusUpdates.length > 0) {
                    // Identify which queue keys correspond to the
                    // succeeded items. We match by (entityType, entityId,
                    // platform='meta_ads') against the live queue because
                    // 'ad' keys include adGroupId which the backend
                    // result doesn't carry. The actual queue drop is
                    // deferred to the end-of-function bulk set() — same
                    // rationale as the Google branches above (keeps
                    // hasChanges() stable during the in-flight save so
                    // the bar doesn't schedule a mid-save exit).
                    const droppedMetaStatusKeys: string[] = [];
                    const seenKeys = new Set<string>();
                    const currentStatusChanges = get().changes.statusChanges;
                    for (const u of succeededStatusUpdates) {
                      for (const [key, change] of currentStatusChanges) {
                        if (
                          !seenKeys.has(key) &&
                          change.platform === 'meta_ads' &&
                          change.entityType === u.entity_type &&
                          change.entityId === u.entity_id
                        ) {
                          droppedMetaStatusKeys.push(key);
                          seenKeys.add(key);
                          break;
                        }
                      }
                    }
                    dispatched.statusChanges.push(...droppedMetaStatusKeys);
                    debug.log('PendingChanges: Tracked succeeded Meta status changes', {
                      count: succeededStatusUpdates.length,
                    });
                  }

                  // Renames: drop from campaignChanges + reset the Meta
                  // store's manualName / manualNameChanges tracking +
                  // PROMOTE the just-saved name into currentName so the
                  // orchestrator's subsequent saveAppConfig sees the new
                  // name in the Meta slice (otherwise the Meta entry in
                  // app_config.campaign_list keeps the OLD name until the
                  // next /meta/campaigns refetch — and applyMetaOverlay
                  // would then surface the stale saved name over the
                  // fresh API value on the next render).
                  const succeededRenameCampaignIds = result.successful_updates
                    .filter((u) => u.operation === 'rename' && u.entity_type === 'campaign')
                    .map((u) => u.entity_id);
                  if (succeededRenameCampaignIds.length > 0) {
                    const renameKeys = succeededRenameCampaignIds.map((id) =>
                      getCampaignChangeKey(id, 'name', 'meta_ads'),
                    );
                    // Queue drop deferred to end-of-function bulk set().
                    // The Meta store reset below stays here — it has to
                    // run before the orchestrator's saveAppConfig reads
                    // the slice.
                    dispatched.campaignChanges.push(...renameKeys);
                    // Map id → new name from the dispatched payload, so
                    // we can promote manualName → currentName on success.
                    const newNameById = new Map(
                      metaRenamePayload.map((p) => [p.entity_id, p.name] as const),
                    );
                    // Reset the Meta store's in-flight tracking +
                    // manualName for each succeeded rename. Imported
                    // lazily for the same reason the rest of this block
                    // is — keep the cross-store coupling at the call
                    // site, not at module-import time.
                    const { useCampaignSettingsMetaAdsStore } = await import(
                      '@/store/campaignSettingsMetaAdsStore'
                    );
                    const metaStore = useCampaignSettingsMetaAdsStore.getState();
                    for (const id of succeededRenameCampaignIds) {
                      metaStore.clearNameChange(id);
                      const newName = newNameById.get(id);
                      metaStore.setCampaignSettings(id, {
                        manualName: null,
                        nameValidationError: null,
                        // Promote so the orchestrator's Meta slice (running
                        // after this in useAccountChanges.onSave) reads
                        // the new name and writes it into the POSTed
                        // app_config. `newName` should always be defined
                        // (the payload is the source of `succeededRenameCampaignIds`),
                        // but guard with optional spread for safety.
                        ...(newName !== undefined ? { currentName: newName } : {}),
                      });
                    }
                    debug.log('PendingChanges: Cleared Meta in-flight state + promoted currentName for succeeded renames', {
                      count: succeededRenameCampaignIds.length,
                    });
                  }

                  // Budget: drop succeeded budget changes from
                  // campaignChanges + reset the Meta store's manualBudget /
                  // manualBudgetChanges tracking + PROMOTE the just-saved
                  // budget into currentBudget so the orchestrator's
                  // subsequent saveAppConfig sees the new budget in the
                  // Meta slice. Same partial-failure-safety pattern as the
                  // rename cleanup above — without it, a partial-failure
                  // save (some statuses succeed, some budgets fail) would
                  // re-send the succeeded budgets on the next retry and
                  // leave the UI in a perpetual "pending" state.
                  //
                  // Promote source: prefer `new_budget` from the backend
                  // result (cent-rounded to what Meta actually stored, so
                  // an entered $5.005 lands as $5.01 in both Meta and our
                  // local view). Fall back to the dispatched value only
                  // when the result omits it (defensive — backend always
                  // populates it for budget ops per MetaBatchUpdateResult).
                  const succeededBudgetUpdates = result.successful_updates.filter(
                    (u) => u.operation === 'budget' && u.entity_type === 'campaign',
                  );
                  if (succeededBudgetUpdates.length > 0) {
                    const budgetKeys = succeededBudgetUpdates.map((u) =>
                      getCampaignChangeKey(u.entity_id, 'budget', 'meta_ads'),
                    );
                    // Queue drop deferred to end-of-function bulk set().
                    // Meta store reset below stays here.
                    dispatched.campaignChanges.push(...budgetKeys);
                    const dispatchedBudgetById = new Map(
                      metaBudgetPayload.map((p) => [p.entity_id, p.daily_budget] as const),
                    );
                    const { useCampaignSettingsMetaAdsStore } = await import(
                      '@/store/campaignSettingsMetaAdsStore'
                    );
                    const metaStore = useCampaignSettingsMetaAdsStore.getState();
                    for (const u of succeededBudgetUpdates) {
                      metaStore.clearBudgetChange(u.entity_id);
                      const promotedBudget =
                        u.new_budget ?? dispatchedBudgetById.get(u.entity_id) ?? null;
                      metaStore.setCampaignSettings(u.entity_id, {
                        manualBudget: null,
                        ...(promotedBudget !== null ? { currentBudget: promotedBudget } : {}),
                      });
                    }
                    debug.log('PendingChanges: Cleared Meta in-flight state + promoted currentBudget for succeeded budget changes', {
                      count: succeededBudgetUpdates.length,
                    });
                  }

                  // Reconcile the React Query app-config cache so the tree +
                  // campaign cards reflect the just-saved Meta changes
                  // immediately, without waiting for the backend's
                  // app_configuration write to propagate. Mirrors the
                  // optimistic-splice pattern the Meta create wizards use;
                  // see docs/FRONTEND_MUTATION_PATTERNS.md. We do this after
                  // the API returns because batchUpdate is a group op — a
                  // pre-write optimistic pass would need per-entity rollback
                  // on partial-failure that the store doesn't otherwise
                  // track. Applying after success is simpler and matches the
                  // partial-failure model (only succeeded_updates get cached).
                  const appConfigKey = ['app-config', memberUuid];
                  const actionByEntity = new Map(
                    metaStatusPayload.map((p) => [
                      `${p.entity_type}:${p.entity_id}`,
                      p.action,
                    ] as const),
                  );
                  for (const u of succeededStatusUpdates) {
                    const action = actionByEntity.get(`${u.entity_type}:${u.entity_id}`);
                    if (!action) continue;
                    const newStatus = metaActionToStatus(action);
                    queryClient.setQueryData<AppConfiguration>(
                      appConfigKey,
                      (prev) => applyMetaStatusChange(
                        prev,
                        u.entity_type as 'campaign' | 'ad_set' | 'ad',
                        u.entity_id,
                        newStatus,
                      ),
                    );
                  }
                  const renameNameById = new Map(
                    metaRenamePayload.map((p) => [p.entity_id, p.name] as const),
                  );
                  for (const id of succeededRenameCampaignIds) {
                    const newName = renameNameById.get(id);
                    if (newName === undefined) continue;
                    queryClient.setQueryData<AppConfiguration>(
                      appConfigKey,
                      (prev) => applyMetaCampaignRename(prev, id, newName),
                    );
                  }
                  const budgetById = new Map(
                    metaBudgetPayload.map((p) => [p.entity_id, p.daily_budget] as const),
                  );
                  for (const u of succeededBudgetUpdates) {
                    const promoted =
                      u.new_budget ?? budgetById.get(u.entity_id) ?? null;
                    if (promoted === null) continue;
                    queryClient.setQueryData<AppConfiguration>(
                      appConfigKey,
                      (prev) => applyMetaCampaignBudget(prev, u.entity_id, promoted),
                    );
                  }
                  if (
                    succeededStatusUpdates.length > 0 ||
                    succeededRenameCampaignIds.length > 0 ||
                    succeededBudgetUpdates.length > 0
                  ) {
                    // Trigger campaign-tree re-derive against the freshly-
                    // patched app-config. Unscoped invalidation because Meta
                    // changes may span multiple campaigns in one batch.
                    queryClient.invalidateQueries({
                      queryKey: ['campaign-from-cache'],
                    });
                  }

                  debug.log('PendingChanges: Meta batch returned', {
                    successful: result.successful_updates.length,
                    failed: result.failed_updates.length,
                  });
                } catch (error) {
                  const msg = error instanceof Error ? error.message : String(error);
                  errors.push(`Meta batch update failed: ${msg}`);
                  debug.error('PendingChanges: Meta batch failed', error);
                }
              })();
              }
            }
          }

          // 6. Trigger a SINGLE data sync at the end (if there were Google Ads changes)
          // Note: We trigger sync even with partial failures to keep UI in sync with
          // the changes that DID succeed.
          //
          // Run alongside the Meta batch promise: both are best-effort with
          // partial-failure tolerance, so awaiting them in parallel with
          // `allSettled` keeps the save latency at max(google, meta) instead
          // of google+meta. Meta has no equivalent data sync trigger — its
          // ingest is a daily cron, not a per-save refresh.
          const finalAwaits: Promise<unknown>[] = [];
          if (madeGoogleAdsChanges) {
            const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
            if (memberUuid) {
              finalAwaits.push(
                (async () => {
                  try {
                    debug.log('PendingChanges: Triggering single data sync for all changes');
                    await googleAdsApi.triggerDataSync(memberUuid);
                    debug.log('PendingChanges: Data sync triggered successfully');
                  } catch (error) {
                    // Don't fail the whole save if data sync fails
                    debug.error('PendingChanges: Data sync trigger failed (non-fatal)', error);
                  }
                })(),
              );
            }
          }
          if (metaSavePromise) {
            finalAwaits.push(metaSavePromise);
          }
          if (finalAwaits.length > 0) {
            await Promise.allSettled(finalAwaits);
          }

          // Bulk-drop all dispatched keys from the live queue in a single
          // set() at the very end of the save. Each per-branch block
          // above pushes succeeded keys into `dispatched` but defers the
          // queue drop here so that hasChanges() stays true throughout
          // the in-flight save. Without that deferral the queue would
          // briefly go empty as items are dispatched per-item, and the
          // SaveChangesBar's visibility useEffect would schedule a
          // mid-save exit — producing a visible flicker (bar slides
          // down, then back up when the user queues a post-snapshot
          // change). See SAVE_CHANGES_BAR_QUEUE_CLEAR_RACE.md for the
          // full chain.
          //
          // The `set({ isSaving: false })` happens AFTER this so the
          // bar's `isSaving` flag stays true through the queue drop —
          // preventing the same flicker via a different path
          // (visibility effect triggered by hasChanges going false
          // while isSaving was already false).
          const hasAnyDispatched =
            dispatched.statusChanges.length > 0 ||
            dispatched.campaignChanges.length > 0 ||
            dispatched.keywordBidChanges.length > 0 ||
            dispatched.channelSettingsChanges.length > 0 ||
            dispatched.flouriSettingChanges.length > 0;
          if (hasAnyDispatched) {
            // Only delete a key if the live Map still holds the same
            // change object we dispatched (identity check against
            // initialSnapshot). A reference mismatch means the user
            // re-edited the same key mid-save — queueXxx overwrites
            // the Map entry with a fresh object — and we must
            // preserve their re-edit.
            //
            // We also filter `dispatched.*` to the actually-dropped
            // subset before returning: SaveChangesBar runs
            // `clearDispatchedChanges(dispatched)` as a safety net,
            // and leaving re-queued keys in `dispatched` would let
            // that safety net wipe the user's re-edit a second time
            // after this function returns.
            const droppedStatus: string[] = [];
            const droppedCampaign: string[] = [];
            const droppedKeywordBid: string[] = [];
            const droppedChannelSettings: string[] = [];
            const droppedFlouriSetting: string[] = [];
            set((state) => {
              const next = {
                statusChanges: new Map(state.changes.statusChanges),
                campaignChanges: new Map(state.changes.campaignChanges),
                keywordBidChanges: new Map(state.changes.keywordBidChanges),
                channelSettingsChanges: new Map(state.changes.channelSettingsChanges),
                flouriSettingChanges: new Map(state.changes.flouriSettingChanges),
              };
              for (const k of dispatched.statusChanges) {
                const live = next.statusChanges.get(k);
                if (live !== undefined && live === initialSnapshot.statusChanges.get(k)) {
                  next.statusChanges.delete(k);
                  droppedStatus.push(k);
                }
              }
              for (const k of dispatched.campaignChanges) {
                const live = next.campaignChanges.get(k);
                if (live !== undefined && live === initialSnapshot.campaignChanges.get(k)) {
                  next.campaignChanges.delete(k);
                  droppedCampaign.push(k);
                }
              }
              for (const k of dispatched.keywordBidChanges) {
                const live = next.keywordBidChanges.get(k);
                if (live !== undefined && live === initialSnapshot.keywordBidChanges.get(k)) {
                  next.keywordBidChanges.delete(k);
                  droppedKeywordBid.push(k);
                }
              }
              for (const k of dispatched.channelSettingsChanges) {
                const live = next.channelSettingsChanges.get(k);
                if (live !== undefined && live === initialSnapshot.channelSettingsChanges.get(k)) {
                  next.channelSettingsChanges.delete(k);
                  droppedChannelSettings.push(k);
                }
              }
              for (const k of dispatched.flouriSettingChanges) {
                const live = next.flouriSettingChanges.get(k);
                if (live !== undefined && live === initialSnapshot.flouriSettingChanges.get(k)) {
                  next.flouriSettingChanges.delete(k);
                  droppedFlouriSetting.push(k);
                }
              }
              return { changes: next };
            });
            // Replace dispatched with the actually-dropped subset so
            // the safety-net clear in SaveChangesBar is a true no-op
            // for survived (re-edited) keys.
            dispatched.statusChanges = droppedStatus;
            dispatched.campaignChanges = droppedCampaign;
            dispatched.keywordBidChanges = droppedKeywordBid;
            dispatched.channelSettingsChanges = droppedChannelSettings;
            dispatched.flouriSettingChanges = droppedFlouriSetting;
            debug.log('PendingChanges: Bulk-dropped dispatched keys', {
              status: dispatched.statusChanges.length,
              campaign: dispatched.campaignChanges.length,
              keywordBid: dispatched.keywordBidChanges.length,
              channelSettings: dispatched.channelSettingsChanges.length,
              flouriSetting: dispatched.flouriSettingChanges.length,
            });
          }

          if (errors.length === 0) {
            set({ lastSavedAt: new Date() });
          }

          set({ isSaving: false });
          return { success: errors.length === 0, errors, dispatched };

        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
          set({ isSaving: false, saveError: errorMsg });
          return { success: false, errors: [errorMsg], dispatched };
        }
      },

      // Clear actions
      clearAllChanges: () => {
        // NOTE: We intentionally do NOT clear discardCallbacks here.
        // Callbacks are registered by components on mount and should persist
        // until those components unmount (via unregisterDiscardCallback cleanup).
        // Clearing callbacks here would break discard functionality when
        // clearAllChanges is called on page navigation.
        set({
          changes: {
            statusChanges: new Map(),
            campaignChanges: new Map(),
            flouriSettingChanges: new Map(),
            keywordBidChanges: new Map(),
            channelSettingsChanges: new Map(),
          },
          saveError: null,
        });
        debug.log('PendingChanges: All changes cleared (callbacks preserved)');
      },

      // Targeted clear used by SaveChangesBar after a successful save.
      // Drops only the keys that were dispatched in this save round —
      // keys queued during the in-flight save survive, so the next save
      // dispatches them. After the per-item cleanup in `saveAllChanges`
      // every dispatched key is already gone, so each .delete here is
      // typically a no-op; the action is kept as a safety net and as the
      // explicit success-path hook for `customOnSave` consumers.
      clearDispatchedChanges: (dispatched: DispatchedKeys) => {
        const noop =
          dispatched.statusChanges.length === 0 &&
          dispatched.campaignChanges.length === 0 &&
          dispatched.keywordBidChanges.length === 0 &&
          dispatched.channelSettingsChanges.length === 0 &&
          dispatched.flouriSettingChanges.length === 0;
        if (noop) return;
        set((state) => {
          const next = {
            statusChanges: new Map(state.changes.statusChanges),
            campaignChanges: new Map(state.changes.campaignChanges),
            keywordBidChanges: new Map(state.changes.keywordBidChanges),
            channelSettingsChanges: new Map(state.changes.channelSettingsChanges),
            flouriSettingChanges: new Map(state.changes.flouriSettingChanges),
          };
          for (const k of dispatched.statusChanges) next.statusChanges.delete(k);
          for (const k of dispatched.campaignChanges) next.campaignChanges.delete(k);
          for (const k of dispatched.keywordBidChanges) next.keywordBidChanges.delete(k);
          for (const k of dispatched.channelSettingsChanges) next.channelSettingsChanges.delete(k);
          for (const k of dispatched.flouriSettingChanges) next.flouriSettingChanges.delete(k);
          return { changes: next };
        });
        debug.log('PendingChanges: Cleared dispatched changes', {
          status: dispatched.statusChanges.length,
          campaign: dispatched.campaignChanges.length,
          keywordBid: dispatched.keywordBidChanges.length,
          channelSettings: dispatched.channelSettingsChanges.length,
          flouriSetting: dispatched.flouriSettingChanges.length,
        });
      },

      clearError: () => {
        set({ saveError: null });
      },

      // Discard callbacks - allow components to register restore functions
      registerDiscardCallback: (key: string, callback: DiscardCallback) => {
        set((state) => {
          const newCallbacks = new Map(state.discardCallbacks);
          newCallbacks.set(key, callback);
          debug.log('PendingChanges: Registered discard callback', { key });
          return { discardCallbacks: newCallbacks };
        });
      },

      unregisterDiscardCallback: (key: string) => {
        set((state) => {
          const newCallbacks = new Map(state.discardCallbacks);
          newCallbacks.delete(key);
          debug.log('PendingChanges: Unregistered discard callback', { key });
          return { discardCallbacks: newCallbacks };
        });
      },

      executeDiscardCallbacks: () => {
        const { discardCallbacks } = get();
        debug.log('PendingChanges: Executing discard callbacks', {
          count: discardCallbacks.size,
        });
        discardCallbacks.forEach((callback, key) => {
          try {
            callback();
            debug.log('PendingChanges: Executed discard callback', { key });
          } catch (error) {
            debug.error('PendingChanges: Discard callback failed', { key, error });
          }
        });
      },

      // Applied keyword bids management
      clearAppliedKeywordBid: (keywordId: string, campaignId: string, adGroupId: string, customerId: string) => {
        const key = getKeywordBidChangeKey(keywordId, campaignId, adGroupId, customerId);
        set((state) => {
          const newAppliedKeywordBids = new Map(state.appliedKeywordBids);
          newAppliedKeywordBids.delete(key);
          debug.log('PendingChanges: Cleared applied keyword bid', { key });
          return { appliedKeywordBids: newAppliedKeywordBids };
        });
      },

      clearAppliedKeywordBidsByKeys: (keys: string[]) => {
        if (keys.length === 0) return;
        set((state) => {
          const newAppliedKeywordBids = new Map(state.appliedKeywordBids);
          for (const key of keys) {
            newAppliedKeywordBids.delete(key);
          }
          debug.log('PendingChanges: Cleared applied keyword bids batch', { count: keys.length });
          return { appliedKeywordBids: newAppliedKeywordBids };
        });
      },

      clearAppliedKeywordBidsForCampaign: (campaignId: string, customerId: string) => {
        // Normalize customerId to match key format
        const normalizedCustomerId = customerId.replace(/\D/g, '');
        set((state) => {
          const newAppliedKeywordBids = new Map(state.appliedKeywordBids);
          let clearedCount = 0;
          for (const [key, bid] of newAppliedKeywordBids) {
            const bidCustomerId = bid.customerId?.replace(/\D/g, '') || '';
            if (bid.campaignId === campaignId && bidCustomerId === normalizedCustomerId) {
              newAppliedKeywordBids.delete(key);
              clearedCount++;
            }
          }
          if (clearedCount > 0) {
            debug.log('PendingChanges: Cleared applied keyword bids for campaign', {
              campaignId,
              customerId: normalizedCustomerId,
              clearedCount
            });
          }
          return { appliedKeywordBids: newAppliedKeywordBids };
        });
      },

      clearAppliedChannelSettings: (campaignId: string, adGroupId: string, customerId: string) => {
        const key = getChannelSettingsChangeKey(campaignId, adGroupId, customerId);
        set((state) => {
          const newAppliedChannelSettings = new Map(state.appliedChannelSettings);
          newAppliedChannelSettings.delete(key);
          debug.log('PendingChanges: Cleared applied channel settings', { key });
          return { appliedChannelSettings: newAppliedChannelSettings };
        });
      },
    }),
    {
      name: 'pending-changes-store',
    }
  )
);
